import { until } from "@vueuse/core";

import type { Ref } from "vue";
import type { RouteLocationNormalized, RouteLocationRaw } from "vue-router";

interface ProfileLike {
    is_platform_admin?: boolean;
}

export interface PlatformAdminGuardDeps {
    /** The `/user/me` profile from the user store; `undefined` until fetched. */
    user: Ref<ProfileLike | undefined>;
    /** Org-less profile fetch (`useUserStore().updateUser`). */
    fetchUser: () => Promise<unknown>;
    /** How long to wait for the profile before giving up. */
    timeoutMs?: number;
}

/**
 * Gate Platform Admin routes on the platform-wide `is_platform_admin` flag.
 *
 * Org-scoped permissions deliberately don't grant access: the flag comes from
 * `/user/me` only.
 *
 * On a direct URL load the profile is not in the store yet, and the store's own
 * boot fetch awaits `router.isReady()`, which resolves only after this guard
 * returns. Waiting alone therefore deadlocked until the timeout and bounced a
 * genuine admin to `/home` on every direct load — so the guard starts the fetch
 * itself. The route is org-less (`requiresOrg: false`), so the org-less fetch
 * is the right one; on in-app navigation the profile is already loaded and no
 * fetch happens.
 */
export async function platformAdminGuard(
    to: RouteLocationNormalized,
    { user, fetchUser, timeoutMs = 10_000 }: PlatformAdminGuardDeps
): Promise<RouteLocationRaw | undefined> {
    if (!to.meta.requiresPlatformAdmin) {
        return undefined;
    }
    if (user.value === undefined) {
        // A failed fetch leaves `user` unset; the timeout below handles that.
        fetchUser().catch(() => undefined);
    }
    try {
        await until(() => user.value !== undefined).toBeTruthy({
            timeout: timeoutMs,
            throwOnTimeout: true,
        });
    } catch {
        return { name: "home" };
    }
    if (!user.value?.is_platform_admin) {
        return { name: "home" };
    }
    return undefined;
}
